import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, onAnimationEnd, prefersReducedMotion, unlockScroll } from '@theme/utilities';

/**
 * Elements that follow the cursor. Marked in `sections/oc-header.liquid` with `data-oc-magnetic`.
 * @type {string}
 */
const MAGNETIC_SELECTOR = '[data-oc-magnetic]';

/**
 * Custom properties the magnetic effect writes. The reference site drives the same two names, and
 * `.oc-header__action` consumes them through
 * `transform: translateX(var(--motion-translateX)) translateY(var(--motion-translateY))`.
 */
const TRANSLATE_X = '--motion-translateX';
const TRANSLATE_Y = '--motion-translateY';

/** Viewport width at which the drawer stops being reachable and the inline menu takes over. */
const DESKTOP_QUERY = '(min-width: 750px)';

/**
 * Clamps a number between two bounds.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Reads a numeric dataset value, falling back when it is missing or unparseable.
 * @param {string | undefined} raw
 * @param {number} fallback
 * @returns {number}
 */
function toNumber(raw, fallback) {
  const parsed = Number.parseFloat(raw ?? '');

  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * The storefront header.
 *
 * Owns four effects:
 *  1. magnetic icons — pointer-driven `--motion-translateX` / `--motion-translateY`, written in a
 *     single `requestAnimationFrame` per pointer move, never straight from the event;
 *  2. the duplicated menu label roll-up — pure CSS, the markup ships the label twice;
 *  3. the sticky scrolled state — `data-scrolled` toggled by an IntersectionObserver on the
 *     header itself, which sits at `top: -1px` precisely so the observer can see it stick;
 *  4. the cutout shadow — pure CSS, its opacity keyed off the same `data-scrolled` flag.
 *
 * It also publishes `--header-height` / `--header-group-height` on `<body>`. Horizon's own
 * `updateHeaderHeights()` only measures a `<header-component>`, so with the native header out of
 * the header group nothing else would keep those in sync.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} inner - The header's layout row.
 * @property {HTMLDialogElement} [drawer] - The mobile navigation dialog.
 * @property {HTMLElement} [drawerPanel] - The dialog's scrolling panel.
 * @property {HTMLElement} [drawerClose] - The dialog's close button.
 * @property {HTMLElement} [menuTrigger] - The hamburger button.
 *
 * @extends {Component<Refs>}
 */
export class OcHeaderComponent extends Component {
  requiredRefs = ['inner'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number | null} */
  #frame = null;

  /** @type {{ x: number, y: number, inside: boolean }} */
  #pointer = { x: 0, y: 0, inside: false };

  /**
   * Current translation applied to each magnetic element. Kept so the element's untransformed
   * centre can be recovered from `getBoundingClientRect()` — measuring the transformed box would
   * feed the offset back into itself and make the icon drift away.
   * @type {WeakMap<Element, { x: number, y: number }>}
   */
  #offsets = new WeakMap();

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  /** @type {boolean} */
  #closingDrawer = false;

  /** @returns {boolean} Whether the magnetic effect should run at all. */
  get magneticEnabled() {
    return this.dataset.magnetic === 'true' && !prefersReducedMotion();
  }

  /** @returns {number} Pull factor, 0–1. */
  get magneticStrength() {
    return clamp(toNumber(this.dataset.magneticStrength, 0.35), 0, 1);
  }

  /** @returns {number} Extra attraction distance in pixels, measured from the icon's edge. */
  get magneticRadius() {
    return Math.max(0, toNumber(this.dataset.magneticRadius, 60));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // `pointermove` is the one interaction the `on:` bindings in component.js do not cover
    // (see the event list in assets/component.js) — hence a single passive listener on the
    // header root rather than one per icon. `pointerleave` is bound declaratively in the
    // section, via `on:pointerleave="/resetMagnetics"`.
    if (this.magneticEnabled) {
      this.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
      window.addEventListener('blur', this.#onPointerLeave, { signal });
    }

    this.#observeSticky();
    this.#observeHeight();

    this.#desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.#desktopQuery.addEventListener('change', this.#onBreakpointChange, { signal });

    const { drawer } = this.refs;
    if (drawer) drawer.addEventListener('close', this.#onDrawerClosed, { signal });
  }

  disconnectedCallback() {
    if (this.#frame !== null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }

    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    const { drawer } = this.refs;
    if (drawer) unlockScroll(drawer);

    this.#abort?.abort();
    this.#abort = null;
    this.#desktopQuery = null;

    super.disconnectedCallback();
  }

  /* ------------------------------------------------------------------ *
   * Effect 1 — magnetic icons
   * ------------------------------------------------------------------ */

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    // Coarse pointers have no hover, so the effect would only ever fire as a jump on tap.
    if (event.pointerType === 'touch') return;

    this.#pointer.x = event.clientX;
    this.#pointer.y = event.clientY;
    this.#pointer.inside = true;
    this.#schedule();
  };

  #onPointerLeave = () => {
    this.#pointer.inside = false;
    this.#schedule();
  };

  /**
   * Declarative counterpart of {@link #onPointerLeave}, bound with `on:pointerleave`.
   */
  resetMagnetics() {
    this.#onPointerLeave();
  }

  #schedule() {
    if (this.#frame !== null) return;

    this.#frame = requestAnimationFrame(this.#applyMagnetics);
  }

  #applyMagnetics = () => {
    this.#frame = null;

    const strength = this.magneticStrength;
    const reach = this.magneticRadius;

    for (const element of this.querySelectorAll(MAGNETIC_SELECTOR)) {
      if (!(element instanceof HTMLElement)) continue;

      if (!this.#pointer.inside || strength === 0) {
        this.#setOffset(element, 0, 0);
        continue;
      }

      const rect = element.getBoundingClientRect();
      const offset = this.#offsets.get(element) ?? { x: 0, y: 0 };

      // Undo the offset already on the element so the centre stays the resting centre.
      const centerX = rect.left + rect.width / 2 - offset.x;
      const centerY = rect.top + rect.height / 2 - offset.y;

      const deltaX = this.#pointer.x - centerX;
      const deltaY = this.#pointer.y - centerY;
      const distance = Math.hypot(deltaX, deltaY);
      const radius = reach + Math.max(rect.width, rect.height) / 2;

      if (radius === 0 || distance > radius) {
        this.#setOffset(element, 0, 0);
        continue;
      }

      // Linear falloff: full pull at the centre, nothing at the edge of the radius.
      const falloff = 1 - distance / radius;

      this.#setOffset(element, deltaX * strength * falloff, deltaY * strength * falloff);
    }
  };

  /**
   * @param {HTMLElement} element
   * @param {number} x
   * @param {number} y
   */
  #setOffset(element, x, y) {
    const previous = this.#offsets.get(element);

    if (previous && Math.abs(previous.x - x) < 0.01 && Math.abs(previous.y - y) < 0.01) return;

    this.#offsets.set(element, { x, y });
    element.style.setProperty(TRANSLATE_X, `${x.toFixed(2)}px`);
    element.style.setProperty(TRANSLATE_Y, `${y.toFixed(2)}px`);
  }

  /* ------------------------------------------------------------------ *
   * Effect 3 — sticky scrolled state
   * ------------------------------------------------------------------ */

  #observeSticky() {
    if (!this.hasAttribute('sticky') || typeof IntersectionObserver === 'undefined') {
      this.dataset.scrolled = 'false';
      return;
    }

    this.dataset.scrolled = 'false';

    // The header rests at `top: -1px`, so the moment it sticks one pixel leaves the viewport and
    // the ratio drops below 1. No scroll listener needed — and it works both when the window
    // scrolls and when `.page-wrapper` is the scroller (base.css does that above 990px).
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.dataset.scrolled = entry.intersectionRatio < 1 ? 'true' : 'false';
      },
      { threshold: [1] }
    );

    this.#stickyObserver.observe(this);
  }

  /* ------------------------------------------------------------------ *
   * Header height custom properties
   * ------------------------------------------------------------------ */

  #observeHeight() {
    this.#syncHeights();

    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => this.#syncHeights());
    this.#resizeObserver.observe(this);
  }

  #syncHeights() {
    const height = this.offsetHeight;
    if (!height) return;

    const group = document.querySelector('#header-group');
    let groupHeight = 0;

    if (group) {
      for (const child of group.children) {
        if (child instanceof HTMLElement) groupHeight += child.offsetHeight;
      }
    }

    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight || height}px`);
  }

  /* ------------------------------------------------------------------ *
   * Mobile drawer
   * ------------------------------------------------------------------ */

  /** @returns {boolean} */
  get menuOpen() {
    return this.hasAttribute('menu-open');
  }

  openMenu() {
    const { drawer, menuTrigger } = this.refs;
    if (!drawer || drawer.open) return;

    this.setAttribute('menu-open', '');
    menuTrigger?.setAttribute('aria-expanded', 'true');

    lockScroll(drawer);
    drawer.showModal();

    // The dialog's own focus step lands on the panel; move it onto the close button instead,
    // matching what assets/theme-drawer.js does for the theme's drawers.
    this.refs.drawerClose?.focus();
  }

  /**
   * Slides the drawer out, then closes it.
   * @returns {Promise<void>}
   */
  async closeMenu() {
    const { drawer } = this.refs;
    if (!drawer || !drawer.open || this.#closingDrawer) return;

    this.#closingDrawer = true;

    if (!prefersReducedMotion()) {
      drawer.classList.add('is-closing');
      await onAnimationEnd(drawer, undefined, { subtree: false });
      drawer.classList.remove('is-closing');
    }

    this.#closingDrawer = false;
    drawer.close();
  }

  /**
   * Closes the drawer when the click lands on the backdrop.
   *
   * The comparison is geometric, not `event.target`-based: `assets/component.js` proxies the event
   * so `target` always reports the element that carries the `on:click` binding — here, the dialog
   * itself — which would make an identity check true for every click inside the panel too.
   * `isClickedOutside` compares the pointer coordinates against the dialog's box instead, exactly
   * as `assets/theme-drawer.js` does.
   *
   * @param {MouseEvent} event
   */
  onDrawerClick(event) {
    const { drawer } = this.refs;
    if (!drawer) return;

    if (isClickedOutside(event, drawer)) this.closeMenu();
  }

  /**
   * Handles Escape ourselves so the slide-out animation still runs.
   * @param {KeyboardEvent} event
   */
  onDrawerKeydown(event) {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;

    event.preventDefault();
    this.closeMenu();
  }

  /** Cleanup shared by the animated close and the browser's native dismissal. */
  #onDrawerClosed = () => {
    const { drawer, menuTrigger } = this.refs;

    this.removeAttribute('menu-open');
    menuTrigger?.setAttribute('aria-expanded', 'false');

    if (drawer) unlockScroll(drawer);
    menuTrigger?.focus();
  };

  #onBreakpointChange = () => {
    if (this.#desktopQuery?.matches && this.refs.drawer?.open) {
      this.refs.drawer.close();
    }
  };
}

if (!customElements.get('oc-header-component')) {
  customElements.define('oc-header-component', OcHeaderComponent);
}
